import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaFileInvoiceDollar, FaHandHoldingUsd, FaCalendarCheck, FaWallet,
  FaBalanceScale, FaLandmark, FaChartPie, FaClipboardList,
  FaExchangeAlt, FaCreditCard, FaChartLine, FaTruck,
  FaFileInvoice, FaFileAlt, FaCheckDouble, FaPlug,
  FaUserCog, FaSitemap, FaSpinner, FaArrowRight,
  FaTachometerAlt, FaCalendarAlt, FaBarcode,
  FaPaperPlane, FaCheck, FaTimes, FaEnvelope,
  FaFileContract, FaSearch, FaDownload, FaEye,
  FaChevronDown, FaChevronRight, FaBolt,
} from 'react-icons/fa';
import './PainelFinanceiro.css';

/* ─── Helpers ─────────────────────────────────────────────────────────── */
const fmtCur = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
};

const fmtDateBR = (d = new Date()) =>
  d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const monthEnd = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${last}`;
};

/* ─── Module Registry ──────────────────────────────────────────────────── */
const MODULES = [
  {
    permissaoId: 'fin_contas_pagar',
    titulo: 'Contas a Pagar',
    descricao: 'Compromissos, boletos e pagamentos',
    rota: '/financeiro/contas-a-pagar',
    Icon: FaFileInvoiceDollar,
    cor: '#e11d48', bgCor: '#fff1f2', borderCor: '#fda4af',
    categoria: 'Dia a Dia', kpiKey: 'pagar',
  },
  {
    permissaoId: 'fin_contas_receber',
    titulo: 'Contas a Receber',
    descricao: 'Recebíveis, faturamento e clientes',
    rota: '/financeiro/contas-a-receber',
    Icon: FaHandHoldingUsd,
    cor: '#059669', bgCor: '#ecfdf5', borderCor: '#6ee7b7',
    categoria: 'Dia a Dia', kpiKey: 'receber',
  },
  {
    permissaoId: 'fin_gerar_boleto',
    titulo: 'Gerar Boleto',
    descricao: 'Emissão e gestão de boletos bancários',
    rota: '/financeiro/boletos',
    Icon: FaBarcode,
    cor: '#0284c7', bgCor: '#e0f2fe', borderCor: '#7dd3fc',
    categoria: 'Dia a Dia',
  },
  {
    permissaoId: 'fin_gastos',
    titulo: 'Controle de Gastos',
    descricao: 'Lançamento e aprovação de despesas',
    rota: '/financeiro/gastos',
    Icon: FaCreditCard,
    cor: '#7c3aed', bgCor: '#f5f3ff', borderCor: '#c4b5fd',
    categoria: 'Dia a Dia',
  },
  {
    permissaoId: 'fin_transacoes',
    titulo: 'Transações',
    descricao: 'Histórico completo de movimentações',
    rota: '/financeiro/transacoes',
    Icon: FaExchangeAlt,
    cor: '#0891b2', bgCor: '#ecfeff', borderCor: '#67e8f9',
    categoria: 'Dia a Dia',
  },
  {
    permissaoId: 'fin_carteira',
    titulo: 'Carteira Digital',
    descricao: 'Saldo real, extrato e visão consolidada',
    rota: '/financeiro/carteira',
    Icon: FaWallet,
    cor: '#0d7377', bgCor: '#e8f4f5', borderCor: '#5eead4',
    categoria: 'Gestão',
  },
  {
    permissaoId: 'fin_fluxo_caixa',
    titulo: 'Fluxo de Caixa',
    descricao: 'Projeção futura de entradas e saídas',
    rota: '/financeiro/fluxo-caixa',
    Icon: FaChartLine,
    cor: '#2563eb', bgCor: '#eff6ff', borderCor: '#93c5fd',
    categoria: 'Gestão',
  },
  {
    permissaoId: 'fin_fechamento',
    titulo: 'Fechamento Mensal',
    descricao: 'Cockpit de consolidação e fechamento',
    rota: '/financeiro/fechamento',
    Icon: FaCalendarCheck,
    cor: '#be185d', bgCor: '#fdf2f8', borderCor: '#f9a8d4',
    categoria: 'Gestão',
  },
  {
    permissaoId: 'fin_rotina_fechamento',
    titulo: 'Rotina de Fechamento',
    descricao: 'Auditoria diária e dossiês automáticos',
    rota: '/financeiro/rotina-fechamento',
    Icon: FaCalendarAlt,
    cor: '#9333ea', bgCor: '#faf5ff', borderCor: '#d8b4fe',
    categoria: 'Gestão',
  },
  {
    permissaoId: 'fin_conciliacao',
    titulo: 'Conciliação Bancária',
    descricao: 'Processamento OFX e conferência bancária',
    rota: '/financeiro/conciliacao',
    Icon: FaCheckDouble,
    cor: '#0f766e', bgCor: '#f0fdfa', borderCor: '#5eead4',
    categoria: 'Gestão',
  },
  {
    permissaoId: 'fin_conexoes',
    titulo: 'Conexões Bancárias',
    descricao: 'Open Finance e integração com bancos',
    rota: '/financeiro/conexoes',
    Icon: FaPlug,
    cor: '#1d4ed8', bgCor: '#eff6ff', borderCor: '#93c5fd',
    categoria: 'Gestão',
  },
  {
    permissaoId: 'fin_dre',
    titulo: 'D.R.E. Gerencial',
    descricao: 'Demonstração do Resultado do Exercício',
    rota: '/financeiro/dre',
    Icon: FaChartPie,
    cor: '#c2410c', bgCor: '#fff7ed', borderCor: '#fdba74',
    categoria: 'Análise',
  },
  {
    permissaoId: 'fin_balanco',
    titulo: 'Balanço Patrimonial',
    descricao: 'Ativos, Passivos e Patrimônio Líquido',
    rota: '/financeiro/balanco',
    Icon: FaBalanceScale,
    cor: '#4338ca', bgCor: '#eef2ff', borderCor: '#a5b4fc',
    categoria: 'Análise',
  },
  {
    permissaoId: 'fin_balancete',
    titulo: 'Balancete de Verificação',
    descricao: 'Relatório contábil consolidado',
    rota: '/financeiro/balancete',
    Icon: FaClipboardList,
    cor: '#065f46', bgCor: '#ecfdf5', borderCor: '#6ee7b7',
    categoria: 'Análise',
  },
  {
    permissaoId: 'fin_rentabilidade',
    titulo: 'Rentabilidade da Frota',
    descricao: 'ROI, custo por KM e eficiência operacional',
    rota: '/financeiro/rentabilidade',
    Icon: FaTruck,
    cor: '#92400e', bgCor: '#fef3c7', borderCor: '#fde68a',
    categoria: 'Análise',
  },
  {
    permissaoId: 'fin_relatorios',
    titulo: 'Relatórios Financeiros',
    descricao: 'Central de relatórios e exportações',
    rota: '/financeiro/relatorios',
    Icon: FaFileAlt,
    cor: '#374151', bgCor: '#f9fafb', borderCor: '#d1d5db',
    categoria: 'Análise',
  },
  {
    permissaoId: 'fin_plano_contas',
    titulo: 'Plano de Contas',
    descricao: 'Estrutura contábil hierárquica',
    rota: '/financeiro/plano-contas',
    Icon: FaSitemap,
    cor: '#1e40af', bgCor: '#eff6ff', borderCor: '#93c5fd',
    categoria: 'Configuração',
  },
  {
    permissaoId: 'fin_gerenciador_patrimonio',
    titulo: 'Gerenciador Patrimonial',
    descricao: 'Gestão de bens, ativos e contrapartidas',
    rota: '/financeiro/patrimonio',
    Icon: FaLandmark,
    cor: '#5b21b6', bgCor: '#f5f3ff', borderCor: '#c4b5fd',
    categoria: 'Configuração',
  },
  {
    permissaoId: 'fin_acerto_motoristas',
    titulo: 'Acerto de Motoristas',
    descricao: 'Prestação de contas e comissões',
    rota: '/financeiro/acerto-motoristas',
    Icon: FaUserCog,
    cor: '#047857', bgCor: '#ecfdf5', borderCor: '#6ee7b7',
    categoria: 'Configuração',
  },
];

const CATEGORIES = ['Dia a Dia', 'Gestão', 'Análise', 'Configuração'];

/* ─── Atalhos rápidos ─────────────────────────────────────────────────── */
const QUICK = [
  { label: 'A Pagar',    rota: '/financeiro/contas-a-pagar',   Icon: FaFileInvoiceDollar, cor: '#e11d48' },
  { label: 'A Receber',  rota: '/financeiro/contas-a-receber', Icon: FaHandHoldingUsd,    cor: '#059669' },
  { label: 'Transações', rota: '/financeiro/transacoes',       Icon: FaExchangeAlt,       cor: '#0891b2' },
  { label: 'Fechamento', rota: '/financeiro/fechamento',       Icon: FaCalendarCheck,     cor: '#be185d' },
  { label: 'DRE',        rota: '/financeiro/dre',              Icon: FaChartPie,          cor: '#c2410c' },
  { label: 'Conciliação',rota: '/financeiro/conciliacao',      Icon: FaCheckDouble,       cor: '#0f766e' },
];

/* ─── Module Card ──────────────────────────────────────────────────────── */
function ModuleCard({ mod, kpis, onClick }) {
  const kpiData = mod.kpiKey ? kpis[mod.kpiKey] : null;
  return (
    <button
      className="pf-module-card"
      onClick={onClick}
      style={{ '--mc': mod.cor, '--mb': mod.bgCor, '--mbd': mod.borderCor }}
    >
      <div className="mc-icon"><mod.Icon /></div>
      <div className="mc-body">
        <span className="mc-title">{mod.titulo}</span>
        <span className="mc-desc">{mod.descricao}</span>
        {kpiData && kpiData.total_pendente > 0 && (
          <div className="mc-kpi">
            <span>{fmtCur(kpiData.total_pendente)}</span>
            {kpiData.total_vencido > 0 && (
              <span className="mc-kpi-alert">{fmtCur(kpiData.total_vencido)} vencido</span>
            )}
          </div>
        )}
      </div>
      <FaChevronRight className="mc-arrow" />
    </button>
  );
}

/* ─── Executive Report Wizard ─────────────────────────────────────────── */
function RelatorioExecutivo() {
  const [tipo,       setTipo]       = useState('receber');
  const [dateStart,  setDateStart]  = useState(monthStart());
  const [dateEnd,    setDateEnd]    = useState(monthEnd());
  const [contas,     setContas]     = useState([]);
  const [selected,   setSelected]   = useState(new Set());
  const [loading,    setLoading]    = useState(false);
  const [step,       setStep]       = useState(1); // 1=filtro 2=seleção 3=compose 4=enviado
  const [email,      setEmail]      = useState('');
  const [nomeDestinatario, setNomeDestinatario] = useState('');
  const [mensagem,   setMensagem]   = useState('');
  const [sending,    setSending]    = useState(false);

  const fetchContas = async () => {
    setLoading(true);
    try {
      const endpoint = tipo === 'pagar' ? '/api/contas-a-pagar' : '/api/contas-a-receber';
      const status   = tipo === 'pagar' ? 'pago' : 'recebido';
      const { data } = await api.get(endpoint, {
        params: { status, dataInicio: dateStart, dataFim: dateEnd, limit: 200 },
      });
      const lista = Array.isArray(data) ? data : (data?.data ?? []);
      setContas(lista);
      setSelected(new Set(lista.map(c => c.id)));
      setStep(2);
    } catch {
      toast.error('Falha ao carregar contas para o relatório.');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === contas.length) setSelected(new Set());
    else setSelected(new Set(contas.map(c => c.id)));
  };

  const totalSelected = useMemo(
    () => contas.filter(c => selected.has(c.id)).reduce((a, c) => a + parseFloat(c.valor || 0), 0),
    [contas, selected]
  );

  const handleSend = async () => {
    if (!email.trim() || selected.size === 0) return;
    setSending(true);
    try {
      const ids = [...selected];
      await api.post('/api/financeiro/relatorio-executivo/email', {
        tipo,
        ids,
        destinatario_email: email.trim(),
        destinatario_nome:  nomeDestinatario.trim() || email.split('@')[0],
        mensagem_personalizada: mensagem.trim() || null,
        periodo: { inicio: dateStart, fim: dateEnd },
      });
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Falha ao enviar relatório.');
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setStep(1);
    setContas([]);
    setSelected(new Set());
    setEmail('');
    setNomeDestinatario('');
    setMensagem('');
  };

  return (
    <div className="pf-rel-wrapper">

      {/* Step indicators */}
      <div className="pf-rel-steps">
        {['Filtrar', 'Selecionar', 'Compor', 'Enviado'].map((s, i) => (
          <div key={s} className={`pf-rel-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}>
            <div className="pf-rel-step-dot">
              {step > i + 1 ? <FaCheck /> : i + 1}
            </div>
            <span>{s}</span>
            {i < 3 && <div className="pf-rel-step-line" />}
          </div>
        ))}
      </div>

      {/* ── Step 1: Filtro ── */}
      {step === 1 && (
        <div className="pf-rel-panel">
          <h4 className="pf-rel-panel-title">
            <FaSearch /> Defina o escopo do relatório
          </h4>

          <div className="pf-rel-tipo">
            <button
              className={`pf-rel-tipo-btn ${tipo === 'receber' ? 'active receber' : ''}`}
              onClick={() => setTipo('receber')}
            >
              <FaHandHoldingUsd />
              Contas Recebidas
            </button>
            <button
              className={`pf-rel-tipo-btn ${tipo === 'pagar' ? 'active pagar' : ''}`}
              onClick={() => setTipo('pagar')}
            >
              <FaFileInvoiceDollar />
              Contas Pagas
            </button>
          </div>

          <div className="pf-rel-dates">
            <div className="pf-rel-date-field">
              <label>De</label>
              <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} />
            </div>
            <div className="pf-rel-date-field">
              <label>Até</label>
              <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
            </div>
          </div>

          <button className="pf-rel-btn-primary" onClick={fetchContas} disabled={loading}>
            {loading ? <FaSpinner className="pf-spin" /> : <FaSearch />}
            {loading ? 'Buscando…' : 'Buscar Contas'}
          </button>
        </div>
      )}

      {/* ── Step 2: Seleção ── */}
      {step === 2 && (
        <div className="pf-rel-panel">
          <div className="pf-rel-panel-header">
            <h4 className="pf-rel-panel-title">
              <FaClipboardList /> {contas.length} conta{contas.length !== 1 ? 's' : ''} encontrada{contas.length !== 1 ? 's' : ''}
            </h4>
            <button className="pf-rel-link" onClick={() => setStep(1)}>← Voltar</button>
          </div>

          {contas.length === 0 ? (
            <div className="pf-rel-empty">
              Nenhuma conta {tipo === 'pagar' ? 'paga' : 'recebida'} no período selecionado.
            </div>
          ) : (
            <>
              <div className="pf-rel-select-all">
                <label>
                  <input type="checkbox"
                    checked={selected.size === contas.length}
                    onChange={toggleAll}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < contas.length; }}
                  />
                  <span>Selecionar todas ({contas.length})</span>
                </label>
                <span className="pf-rel-total-tag">
                  {selected.size} selecionadas · {fmtCur(totalSelected)}
                </span>
              </div>

              <div className="pf-rel-list">
                {contas.map(c => (
                  <label key={c.id} className={`pf-rel-item ${selected.has(c.id) ? 'checked' : ''}`}>
                    <input type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleItem(c.id)}
                    />
                    <span className="pf-rel-item-desc" title={c.descricao}>{c.descricao}</span>
                    <span className="pf-rel-item-date">{fmtDate(c.data_vencimento)}</span>
                    <span className="pf-rel-item-val">{fmtCur(c.valor)}</span>
                  </label>
                ))}
              </div>

              <button
                className="pf-rel-btn-primary"
                onClick={() => setStep(3)}
                disabled={selected.size === 0}
              >
                <FaEnvelope /> Compor E-mail ({selected.size} contas)
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Step 3: Composição ── */}
      {step === 3 && (
        <div className="pf-rel-panel">
          <div className="pf-rel-panel-header">
            <h4 className="pf-rel-panel-title">
              <FaEnvelope /> Compor Relatório Executivo
            </h4>
            <button className="pf-rel-link" onClick={() => setStep(2)}>← Voltar</button>
          </div>

          <div className="pf-rel-summary-badge">
            <div>
              <span>{selected.size} conta{selected.size !== 1 ? 's' : ''}</span>
              <strong>{fmtCur(totalSelected)}</strong>
            </div>
            <div className={`pf-rel-tipo-badge ${tipo}`}>
              {tipo === 'pagar' ? 'Contas Pagas' : 'Contas Recebidas'}
            </div>
          </div>

          <div className="pf-rel-form">
            <div className="pf-rel-form-row">
              <label>E-mail do destinatário *</label>
              <input
                type="email"
                placeholder="financeiro@empresa.com.br"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="pf-rel-form-row">
              <label>Nome do destinatário</label>
              <input
                type="text"
                placeholder="Ex: Maria Clara · CFO"
                value={nomeDestinatario}
                onChange={e => setNomeDestinatario(e.target.value)}
              />
            </div>
            <div className="pf-rel-form-row">
              <label>Mensagem personalizada <span>(opcional)</span></label>
              <textarea
                rows={3}
                placeholder="Segue o relatório de contas do período para sua análise…"
                value={mensagem}
                onChange={e => setMensagem(e.target.value)}
                maxLength={400}
              />
            </div>
          </div>

          <button
            className="pf-rel-btn-send"
            onClick={handleSend}
            disabled={!email.trim() || selected.size === 0 || sending}
          >
            {sending ? <FaSpinner className="pf-spin" /> : <FaPaperPlane />}
            {sending ? 'Enviando…' : 'Enviar Relatório Executivo'}
          </button>
        </div>
      )}

      {/* ── Step 4: Enviado ── */}
      {step === 4 && (
        <div className="pf-rel-panel pf-rel-success">
          <div className="pf-rel-success-icon">
            <FaCheck />
          </div>
          <h4>Relatório enviado!</h4>
          <p>O relatório executivo foi enviado para <strong>{email}</strong> com {selected.size} lançamentos totalizando <strong>{fmtCur(totalSelected)}</strong>.</p>
          <button className="pf-rel-btn-primary" onClick={reset}>
            Gerar Novo Relatório
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────────── */
export default function PainelFinanceiro() {
  const navigate   = useNavigate();
  const [user,          setUser]         = useState(null);
  const [perms,         setPerms]        = useState(new Set());
  const [carteira,      setCarteira]     = useState(null);
  const [kpiPagar,      setKpiPagar]     = useState(null);
  const [kpiReceber,    setKpiReceber]   = useState(null);
  const [loading,       setLoading]      = useState(true);
  const [catOpen,       setCatOpen]      = useState({ 'Dia a Dia': true });

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(u);
      if (u?.id) load(u.id);
    } catch { setLoading(false); }
  }, []);

  const load = useCallback(async (userId) => {
    try {
      const [permRes, cartRes, kpPRes, kpRRes] = await Promise.allSettled([
        api.get(`/api/permissoes/${userId}`),
        api.get('/api/financeiro/carteira'),
        api.get('/api/financeiro/kpis-pagar'),
        api.get('/api/financeiro/kpis-receber'),
      ]);
      if (permRes.status === 'fulfilled') {
        const arr = Array.isArray(permRes.value.data) ? permRes.value.data : [];
        setPerms(new Set(arr));
      }
      if (cartRes.status === 'fulfilled')  setCarteira(cartRes.value.data);
      if (kpPRes.status === 'fulfilled')   setKpiPagar(kpPRes.value.data);
      if (kpRRes.status === 'fulfilled')   setKpiReceber(kpRRes.value.data);
    } catch { toast.error('Erro ao carregar painel financeiro.'); }
    finally { setLoading(false); }
  }, []);

  const visibleModules = useMemo(() => {
    const isAdm = ['admin_geral', 'admin', 'financeiro'].includes(user?.perfil);
    return MODULES.filter(m => {
      if (isAdm && perms.has('financeiro_view')) return true;
      return perms.has(m.permissaoId);
    });
  }, [perms, user]);

  const modulesByCategory = useMemo(() =>
    CATEGORIES.map(cat => ({ cat, mods: visibleModules.filter(m => m.categoria === cat) }))
      .filter(g => g.mods.length > 0),
    [visibleModules]
  );

  const kpis    = useMemo(() => ({ pagar: kpiPagar, receber: kpiReceber }), [kpiPagar, kpiReceber]);
  const saldo   = carteira?.saldo ?? null;
  const txs     = carteira?.ultimas_transacoes ?? [];

  const toggleCat = (cat) => setCatOpen(prev => ({ ...prev, [cat]: !prev[cat] }));

  if (loading) return (
    <div className="pf-loading">
      <FaSpinner className="pf-spin" />
      <p>Carregando painel financeiro…</p>
    </div>
  );

  return (
    <div className="pf-root">

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <div className="pf-hero">
        <div className="pf-hero-bg" />

        <div className="pf-hero-content">
          <div className="pf-hero-left">
            <p className="pf-greeting">
              {greeting()}, <strong>{user?.nome?.split(' ')[0] || 'usuário'}</strong>
            </p>
            <p className="pf-date">{fmtDateBR()}</p>

            <div className="pf-saldo-block">
              <span className="pf-saldo-label">Saldo em Caixa</span>
              <span className={`pf-saldo-val ${saldo === null ? '' : saldo >= 0 ? 'pos' : 'neg'}`}>
                {saldo !== null ? fmtCur(saldo) : '—'}
              </span>
              <span className="pf-saldo-hint">atualizado agora</span>
            </div>
          </div>

          <div className="pf-hero-kpis">
            {kpiPagar && (
              <button className="pf-hkpi pf-hkpi--pagar" onClick={() => navigate('/financeiro/contas-a-pagar')}>
                <div className="pf-hkpi-icon"><FaFileInvoiceDollar /></div>
                <div>
                  <span className="pf-hkpi-label">A Pagar</span>
                  <strong className="pf-hkpi-val">{fmtCur(kpiPagar.total_pendente)}</strong>
                  {kpiPagar.total_vencido > 0 && (
                    <span className="pf-hkpi-alert">{fmtCur(kpiPagar.total_vencido)} vencido</span>
                  )}
                </div>
                <FaArrowRight className="pf-hkpi-arrow" />
              </button>
            )}
            {kpiReceber && (
              <button className="pf-hkpi pf-hkpi--receber" onClick={() => navigate('/financeiro/contas-a-receber')}>
                <div className="pf-hkpi-icon"><FaHandHoldingUsd /></div>
                <div>
                  <span className="pf-hkpi-label">A Receber</span>
                  <strong className="pf-hkpi-val">{fmtCur(kpiReceber.total_pendente)}</strong>
                  {kpiReceber.total_vencido > 0 && (
                    <span className="pf-hkpi-alert">{fmtCur(kpiReceber.total_vencido)} vencido</span>
                  )}
                </div>
                <FaArrowRight className="pf-hkpi-arrow" />
              </button>
            )}
            {txs.length > 0 && (
              <button className="pf-hkpi pf-hkpi--last" onClick={() => navigate('/financeiro/transacoes')}>
                <div className="pf-hkpi-icon"><FaExchangeAlt /></div>
                <div>
                  <span className="pf-hkpi-label">Última transação</span>
                  <strong className={`pf-hkpi-val ${txs[0]?.tipo}`}>
                    {txs[0]?.tipo === 'entrada' ? '+' : '−'}{fmtCur(txs[0]?.valor)}
                  </strong>
                  <span className="pf-hkpi-alert" style={{ color: '#94a3b8' }}>{txs[0]?.data}</span>
                </div>
                <FaArrowRight className="pf-hkpi-arrow" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── ATALHOS RÁPIDOS ───────────────────────────────────────────── */}
      <div className="pf-quick-row">
        <span className="pf-quick-label"><FaBolt /> Acesso Rápido</span>
        <div className="pf-quick-pills">
          {QUICK.map(q => (
            <button
              key={q.rota}
              className="pf-quick-pill"
              style={{ '--qc': q.cor }}
              onClick={() => navigate(q.rota)}
            >
              <q.Icon /> {q.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── MÓDULOS ───────────────────────────────────────────────────── */}
      {visibleModules.length === 0 ? (
        <div className="pf-no-access">
          <FaTachometerAlt />
          <h3>Nenhum módulo disponível</h3>
          <p>Solicite acesso ao administrador.</p>
        </div>
      ) : (
        <div className="pf-categories">
          {modulesByCategory.map(({ cat, mods }) => (
            <section key={cat} className="pf-cat-section">
              <button className="pf-cat-header" onClick={() => toggleCat(cat)}>
                <h2 className="pf-cat-title">{cat}</h2>
                <span className="pf-cat-count">{mods.length}</span>
                <FaChevronDown className={`pf-cat-chevron ${catOpen[cat] ? 'open' : ''}`} />
              </button>
              {catOpen[cat] !== false && (
                <div className="pf-modules-grid">
                  {mods.map(mod => (
                    <ModuleCard
                      key={mod.permissaoId}
                      mod={mod}
                      kpis={kpis}
                      onClick={() => navigate(mod.rota)}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {/* ── RELATÓRIO EXECUTIVO ───────────────────────────────────────── */}
      <section className="pf-executive-section">
        <div className="pf-executive-header">
          <div className="pf-executive-badge">
            <FaFileContract /> Relatório Executivo
          </div>
          <h2 className="pf-executive-title">Gerador de Relatório por E-mail</h2>
          <p className="pf-executive-desc">
            Selecione o tipo, período e contas, compose a mensagem e envie um relatório
            profissional diretamente para os interessados.
          </p>
          <div className="pf-executive-rules">
            <span>✓ Apenas contas pagas ou recebidas</span>
            <span>✓ Template executivo em HTML</span>
            <span>✓ Registro de auditoria automático</span>
          </div>
        </div>
        <RelatorioExecutivo />
      </section>

      {/* ── TRANSAÇÕES RECENTES ───────────────────────────────────────── */}
      {txs.length > 0 && (
        <section className="pf-recent">
          <div className="pf-recent-header">
            <h2 className="pf-cat-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Últimas Movimentações
            </h2>
            <button className="pf-ver-mais" onClick={() => navigate('/financeiro/transacoes')}>
              Ver tudo <FaArrowRight />
            </button>
          </div>
          <div className="pf-tx-list">
            {txs.slice(0, 6).map(tx => (
              <div key={tx.id} className="pf-tx-item">
                <span className={`pf-tx-dot ${tx.tipo}`} />
                <span className="pf-tx-desc">{tx.descricao}</span>
                <span className="pf-tx-date">{tx.data}</span>
                <span className={`pf-tx-val ${tx.tipo}`}>
                  {tx.tipo === 'entrada' ? '+' : '−'}{fmtCur(tx.valor)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
}